import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Modal } from './Modal';
import Button from './ui/Button';
import { FIELD } from './ui/tokens';
// One declaration per type: User and Role come from the API modules that own
// them (this file used to redeclare both).
import { User } from '../lib/usersApi';
import { Role } from '../lib/rolesApi';

interface AddUserModalProps {
  user?: User | null;
  roles: Role[];
  onClose: () => void;
  onSubmit: (userData: any) => Promise<void>;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ user, roles, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: roles.find(r => r.name === 'user')?.name || roles[0]?.name || '',
    isActive: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        password: '',
        role: user.role,
        isActive: user.isActive
      });
    } else if (roles.length > 0 && !formData.role) {
      // Set default role if not set
      setFormData(prev => ({
        ...prev,
        role: roles.find(r => r.name === 'user')?.name || roles[0]?.name || ''
      }));
    }
  }, [user, roles]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2 || formData.firstName.trim().length > 50) {
      newErrors.firstName = 'First name must be between 2 and 50 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName.trim())) {
      newErrors.firstName = 'First name can only contain letters and spaces';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2 || formData.lastName.trim().length > 50) {
      newErrors.lastName = 'Last name must be between 2 and 50 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName.trim())) {
      newErrors.lastName = 'Last name can only contain letters and spaces';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please provide a valid email';
    }

    // Password validation (only for new users)
    if (!user) {
      if (!formData.password.trim()) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters long';
      } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).*$/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      setErrors({}); // Clear any existing errors

      try {
        const submitData: any = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          role: formData.role,
          isActive: formData.isActive
        };

        // Include password for new users
        if (!user && formData.password) {
          submitData.password = formData.password;
        }

        await onSubmit(submitData);
      } catch (error: any) {
        // Handle validation errors from server
        if (error.isValidationError && error.validationErrors) {
          const serverErrors: { [key: string]: string } = {};
          error.validationErrors.forEach((validationError: any) => {
            if (validationError.path && validationError.msg) {
              serverErrors[validationError.path] = validationError.msg;
            }
          });
          setErrors(serverErrors);
        } else {
          // Handle other errors - you might want to show a general error message
          console.error('Form submission error:', error);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const fieldClass = (name: string) => `${FIELD} h-10 ${errors[name] ? 'border-red-300' : 'border-slate-200'}`;

  // The app's Modal and Button, the same form as Vendor List and Client List. This was a
  // Figma export: a fixed 610x394 box with every field absolutely positioned, typed text in
  // grey (#999), an all-caps submit, and no way out but the X.
  return (
    <Modal isOpen title={user ? 'Edit user' : 'Add new user'} onClose={onClose} maxWidthClass="max-w-xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="user-first-name" className="mb-1 block text-sm font-medium text-slate-700">
              First name
            </label>
            <input
              id="user-first-name"
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder="Enter first name"
              className={fieldClass('firstName')}
            />
            {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>}
          </div>

          <div>
            <label htmlFor="user-last-name" className="mb-1 block text-sm font-medium text-slate-700">
              Last name
            </label>
            <input
              id="user-last-name"
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder="Enter last name"
              className={fieldClass('lastName')}
            />
            {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>}
          </div>

          <div>
            <label htmlFor="user-email" className="mb-1 block text-sm font-medium text-slate-700">
              Email
            </label>
            <input
              id="user-email"
              type="email"
              name="email"
              autoComplete="off"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email"
              className={fieldClass('email')}
            />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
          </div>

          {!user && (
            <div>
              <label htmlFor="user-password" className="mb-1 block text-sm font-medium text-slate-700">
                Password
              </label>
              <div className="relative">
                {/* `new-password` stops Chrome filling the signed-in admin's own saved login into
                    a NEW user's form, which it did on QA on 2026-09-14. */}
                <input
                  id="user-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="At least 6 characters"
                  className={`${fieldClass('password')} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700"
                >
                  {showPassword ? <Eye aria-hidden="true" className="h-4 w-4" /> : <EyeOff aria-hidden="true" className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="user-role" className="mb-1 block text-sm font-medium text-slate-700">
            Role
          </label>
          <select id="user-role" name="role" value={formData.role} onChange={handleInputChange} className={fieldClass('role')}>
            <option value="">Select role</option>
            {roles
              .filter(role => role.isActive)
              .map((role) => (
                <option key={role._id} value={role.name}>
                  {role.displayName}
                </option>
              ))}
          </select>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {user ? 'Update user' : 'Create user'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddUserModal;
